import { cn } from "@newxsystems/shared";
import { AbyssCardLink } from "../../../../modules/content/components/AbyssCardLink/AbyssCardLink";
import { AbyssCommonContent } from "../../../../modules/content/components/AbyssCommonContent/AbyssCommonContent";
import { AbyssMedia } from "../../../../modules/content/components/AbyssMedia/AbyssMedia";
import { AbyssRichText } from "../../../../modules/content/components/AbyssRichText/AbyssRichText";
import { AbyssSection } from "../../../../modules/content/components/AbyssSection/AbyssSection";
import type { ITestimonialGridBlockProps } from "./TestimonialGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const TestimonialGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITestimonialGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <AbyssSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <AbyssCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div
          className={cn(
            "grid grid-cols-1 border-l border-t border-iron sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <AbyssCardLink
              className={cn(
                "flex flex-col justify-between border-b border-r border-t-0 border-iron bg-obsidian/35 p-6 text-frost md:min-h-80 md:p-8",
                spans[Math.min(item.columnSpan || 1, columnCount || 3) as keyof typeof spans],
              )}
              key={index}
              link={item.cardLink}
            >
              <div className="flex items-center gap-4">
                {item.portrait ? (
                  <AbyssMedia
                    aspectRatio="1:1"
                    className="size-14 shrink-0"
                    media={item.portrait.image}
                    sizes="56px"
                  />
                ) : null}
                <div>
                  <p className="font-abyss-mono text-sm font-medium">{item.name}</p>
                  {item.attribution ? (
                    <p className="mt-1 font-abyss-mono text-xs text-frost/75">{item.attribution}</p>
                  ) : null}
                </div>
              </div>
              <blockquote className="mt-10">
                <AbyssRichText
                  className="font-abyss-body text-2xl leading-snug text-frost md:text-3xl"
                  content={item.quote}
                />
              </blockquote>
            </AbyssCardLink>
          ))}
        </div>
      </div>
    </AbyssSection>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
